"use client";

import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import Divider from '../Divider';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  className?: string;
  children: React.ReactNode;
  animate?: boolean;
  /** Full-screen sheet below `lg`. */
  fullScreenOnMobile?: boolean;
  /** Always cover the viewport (lightbox). Skips card chrome/padding. */
  fullScreen?: boolean;
}

const DEFAULT_PANEL =
  'rounded-2xl p-4 bg-white shadow-2xl max-w-2xl mx-4';
const FULLSCREEN_PANEL =
  'fixed inset-0 m-0 h-dvh w-full max-w-none max-h-none rounded-none p-0 bg-transparent shadow-none overflow-visible';
const MOBILE_SHEET_PANEL = `
  rounded-none p-4 bg-white shadow-2xl
  max-lg:!fixed max-lg:!inset-0 max-lg:!m-0 max-lg:!h-dvh max-lg:!w-full
  max-lg:!max-w-none max-lg:!max-h-none max-lg:!rounded-none max-lg:!overflow-y-auto
  lg:relative lg:rounded-2xl lg:max-w-2xl lg:mx-4 lg:h-auto lg:max-h-[90vh] lg:overflow-y-auto
`;

function getAnimationClass(animate: boolean, isOpen: boolean) {
  if (!animate) return '';
  return isOpen ? 'animate-slide-down' : 'animate-slide-up';
}

function getPanelLayoutClass(fullScreen: boolean, useMobileSheet: boolean) {
  if (fullScreen) return FULLSCREEN_PANEL;
  if (useMobileSheet) return MOBILE_SHEET_PANEL;
  return DEFAULT_PANEL;
}

function getOverlayFlexClass(fullScreen: boolean, useMobileSheet: boolean) {
  if (fullScreen) return 'items-stretch justify-stretch';
  if (useMobileSheet) {
    return 'items-stretch justify-stretch lg:items-center lg:justify-center';
  }
  return 'items-center justify-center';
}

function useModalVisibility(isOpen: boolean, animate: boolean) {
  const [shouldRender, setShouldRender] = useState(isOpen);

  if (isOpen && !shouldRender) {
    setShouldRender(true);
  } else if (!isOpen && !animate && shouldRender) {
    setShouldRender(false);
  }

  useEffect(() => {
    if (isOpen || !animate || !shouldRender) return;

    const timeout = setTimeout(() => {
      setShouldRender(false);
    }, 300);

    return () => clearTimeout(timeout);
  }, [isOpen, shouldRender, animate]);

  return shouldRender;
}

function useBodyScrollLock(isOpen: boolean) {
  useEffect(() => {
    if (!isOpen) return;

    const { body } = document;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const previousOverflow = body.style.overflow;
    const previousPaddingRight = body.style.paddingRight;
    const computedPaddingRight =
      Number.parseInt(window.getComputedStyle(body).paddingRight, 10) || 0;

    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${computedPaddingRight + scrollbarWidth}px`;
    }

    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPaddingRight;
    };
  }, [isOpen]);
}

function useEscapeToClose(isOpen: boolean, onClose: () => void) {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
}

function ModalHeader({
  title,
  onClose,
}: Readonly<{ title?: string; onClose: () => void }>) {
  return (
    <div className="flex flex-col px-4 sm:px-8 py-4">
      <div className="flex items-center justify-between">
        {title ? (
          <h2 className="text-lg font-semibold">{title}</h2>
        ) : (
          <span />
        )}

        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          aria-label="Close modal"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      {title ? <Divider /> : null}
    </div>
  );
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  className = '',
  children,
  animate = true,
  fullScreenOnMobile = true,
  fullScreen = false,
}) => {
  const shouldRender = useModalVisibility(isOpen, animate);
  useBodyScrollLock(isOpen);
  useEscapeToClose(isOpen, onClose);

  const useMobileSheet = fullScreenOnMobile && !fullScreen;
  const animationClass = getAnimationClass(animate, isOpen);
  const panelLayoutClass = getPanelLayoutClass(fullScreen, useMobileSheet);
  const overlayFlexClass = getOverlayFlexClass(fullScreen, useMobileSheet);

  if (!shouldRender) return null;

  return (
    <div className={`fixed inset-0 z-50 flex ${overlayFlexClass}`}>
      <button
        type="button"
        aria-label="Close modal"
        onClick={onClose}
        className={`
          absolute inset-0 bg-black/50
          transition-opacity duration-300
          ${isOpen ? 'opacity-100' : 'opacity-0'}
          ${useMobileSheet ? 'max-lg:hidden' : ''}
          ${fullScreen ? 'bg-black/80' : ''}
        `}
      />

      <div
        className={`
          relative z-10
          ${animationClass}
          ${className}
          ${panelLayoutClass}
        `}
      >
        {!fullScreen && <ModalHeader title={title} onClose={onClose} />}

        <div className={fullScreen ? 'h-full w-full' : 'px-4 sm:px-8 pb-8'}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
